import { cardSizeFrom } from "@/board/board-utils";
import type { Board, BoardData, Swimlane } from "@/model/board";
import { isBoardWithObjectives } from "@/model/board";
import type { FlexType } from "@/model/flexboard";
import type { Session } from "@/model/session";
import { emptyIterationState } from "@/store/board";
import { useTeamStore } from "@/store/team";

import type { ServerBoard, ServerFlexBoard, ServerSwimlane } from "../serverModel";
import type { BackendReadSession } from "./BackendReadSession";

export function mapBoard(
  board: ServerBoard | ServerFlexBoard,
  session: Session,
  flexTypes: FlexType[],
  almSourceProvider: BackendReadSession["getAlmSource"],
): Board {
  return board.board_type === "flex"
    ? mapFlexBoard(board, flexTypes)
    : mapNonFlexBoard(board, session, almSourceProvider);
}

export function mapFlexBoard(board: ServerFlexBoard, flexTypes: FlexType[]): BoardData<"flex"> {
  return {
    ...baseBoardData(board),
    flexType: flexTypes.find((type) => type.id === board.flexboard_type)!,
    name: board.name,
    almSources: [],
  } as BoardData<"flex">;
}

function mapNonFlexBoard(
  board: ServerBoard,
  session: Session,
  almSourceProvider: BackendReadSession["getAlmSource"],
): Board {
  const alm = almSourceProvider(board.board_type, { teamId: board.user_id, artId: board.art_id });
  return {
    ...baseBoardData(board),
    team:
      board.user_id === null
        ? undefined
        : useTeamStore().teams.find((t) => t.id === "" + board.user_id),
    ...(board.board_type === "team"
      ? { iterationStates: session.iterations.map(emptyIterationState) }
      : {}),
    ...(isBoardWithObjectives(board.board_type) ? { objectives: [], stretchObjectives: [] } : {}),
    almSources: alm ? alm.sources.map(({ id, name }) => ({ id, name })) : [],
    artId: board.art_id ? "" + board.art_id : undefined,
    swimlanes: board.swimlanes?.map(mapSwimlane),
  } as unknown as Board;
}

function mapSwimlane(swimlane: ServerSwimlane): Swimlane {
  switch (swimlane.type) {
    case "team":
      return { type: "team", teamIds: ["" + swimlane.team_ids![0]] };
    case "external":
      return {
        type: "external",
        teamIds: swimlane.team_ids!.map((id) => "" + id),
        name: swimlane.name ?? "",
      };
    case "other_arts":
      return { type: "otherArts" };
    default:
      return { type: swimlane.type };
  }
}

function baseBoardData(board: ServerBoard | ServerFlexBoard): Partial<Board> {
  return {
    id: board.board_id,
    type: board.board_type,
    cardSize: cardSizeFrom({ factor: 1 }),
    cards: {},
    maxZ: 0,
    state: { state: "inited", finishedTicks: 0, loadedAt: 0 },
    selected: {},
    shapes: board.shapes || [],
  };
}
